import { Box, Button, Chip, Dialog, DialogActions, DialogContent, Divider, Grid, IconButton, Slide, Stack, Typography, } from "@mui/material";
import { Close, EmailOutlined, LocalPhoneOutlined, } from "@mui/icons-material";
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import { forwardRef, memo } from "react";
import { green, grey, red } from "@mui/material/colors";

const Transition = forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const displayValue = (value, fallback = "—") => {
    if (value === null || value === undefined || String(value).trim() === "") {
        return fallback;
    }
    return value;
};

const buildAddress = (data, type) => {
    const prefix = type === "billing" ? "billing_add_" : "shipping_add_";
    return [data?.[`${prefix}1`], data?.[`${prefix}2`], data?.[`${prefix}3`], [data?.[`${prefix}state`], data?.[`${prefix}zip`]].filter(Boolean).join(" "),].filter(Boolean);
};

const SectionTitle = memo(function SectionTitle({ children }) {
    return (
        <Stack direction="row" spacing={1} sx={{ mb: 1, alignItems: "center" }}>
            <Typography variant="caption" fontWeight={700} color="primary.main" sx={{ textTransform: "uppercase", letterSpacing: "0.06em", whiteSpace: "nowrap", }} > {children} </Typography>
            <Divider sx={{ flex: 1 }} />
        </Stack>
    );
});

const InfoItem = memo(function InfoItem({ label, value, icon, color = "textPrimary" }) {
    return (
        <Box
            sx={{ minWidth: 0, py: 0.5 }}>
            <Typography variant="caption" color="textSecondary" sx={{ display: "block", lineHeight: 1.2, mb: 0.25 }}>{label}</Typography>

            <Stack direction="row" spacing={0.6} sx={{ minWidth: 0, alignItems: "center" }}>
                {icon && (
                    <Box sx={{ display: "flex", color: "textSecondary", flexShrink: 0 }}>
                        {icon}
                    </Box>
                )}

                <Typography variant="body2" fontWeight={500} color={color} noWrap sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
                    {displayValue(value)}
                </Typography>
            </Stack>
        </Box>
    );
});

const AddressCard = memo(function AddressCard({ title, address }) {
    return (
        <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.5, p: 1.25, height: "100%", backgroundColor: grey[50] }}>
            <Typography variant="caption" color="textSecondary" fontWeight={700} sx={{ textTransform: "uppercase", letterSpacing: "0.04em", }}>{title}</Typography>
            <Typography variant="body2" sx={{ mt: 0.6, lineHeight: 1.5, whiteSpace: "pre-line", color: address.length ? "textPrimary" : "textDisabled", }} >{address.length ? address.join("\n") : "No address available"}</Typography>
        </Box>
    );
});

function VendorDetails({ open, data, handleClose }) {
    if (!open || !data) return null;
    const billingAddress = buildAddress(data, "billing");
    const shippingAddress = buildAddress(data, "shipping");
    const isActive = data.status === "Active";
    const handleSelfClose = () => {
        handleClose(null);
    };
    const handleEditClose = () => {
        handleClose("edit");
    };

    return (
        <Dialog open={open} onClose={handleSelfClose} TransitionComponent={Transition} fullWidth maxWidth="sm" scroll="paper" PaperProps={{ sx: { borderRadius: 2.5, overflow: "hidden", width: "100%", m: { xs: 1.5, sm: 2 }, }, }} >
            <Box sx={{ px: 2.25, py: 1.75, borderBottom: "1px solid", borderColor: "divider", backgroundColor: grey[50] }}>
                <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "space-between" }}>
                    <Box>
                        <Typography variant="h6" fontWeight={700} noWrap sx={{ overflow: "hidden", textOverflow: "ellipsis", }} > {displayValue(data.name, "Unnamed Vendor")} </Typography>
                        <Stack direction="row" spacing={1} sx={{ alignItems: "center", mt: 0.4 }}>
                            <Typography variant="caption" color="textSecondary">ID: {displayValue(data.vendor_id)}</Typography>
                            <Typography variant="caption" color="textDisabled">•</Typography>
                            <Typography variant="caption" color="textSecondary">{displayValue(data.created)}</Typography>
                            <Chip
                                size="small"
                                icon={isActive ? (<CheckCircleOutlineOutlinedIcon sx={{ fontSize: 15 }} />) : (<ErrorOutlineOutlinedIcon sx={{ fontSize: 15 }} />)}
                                label={displayValue(data.status, "Unknown")}
                                sx={{
                                    height: 25,
                                    fontSize: 11,
                                    color: isActive ? green[700] : red[700],
                                    backgroundColor: isActive ? green[50] : red[50],
                                    border: "1px solid",
                                    borderColor: isActive ? green[100] : red[100],
                                    "& .MuiChip-icon": { color: isActive ? green[600] : red[600] },
                                }}
                            />
                        </Stack>
                    </Box>
                    <Stack>
                        <IconButton size="small" onClick={handleSelfClose} aria-label="Close"><Close fontSize="small" /></IconButton>
                    </Stack>
                </Box>
            </Box>

            <DialogContent sx={{ px: 2.25, py: 1.75, "&:first-of-type": { pt: 1.75, } }}>
                <SectionTitle>Contact</SectionTitle>
                <Grid container spacing={1.25} sx={{ mb: 1.75 }}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <InfoItem label="Phone" value={data.phone} icon={<LocalPhoneOutlined sx={{ fontSize: 17 }} />} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <InfoItem label="Email" value={data.email} icon={<EmailOutlined sx={{ fontSize: 17 }} />} />
                    </Grid>
                </Grid>

                <SectionTitle>Account</SectionTitle>
                <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.5, px: 1.5, py: 1, mb: 1.75, }} >
                    <Grid container spacing={1}>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <InfoItem label="Balance" value={data.balance} />
                        </Grid>

                        <Grid size={{ xs: 12, sm: 4 }}>
                            <InfoItem label="Opening Date" value={data.opening_date} />
                        </Grid>

                        <Grid size={{ xs: 12, sm: 4 }}>
                            <InfoItem label="Opening Balance" value={data.opening_balance} />
                        </Grid>
                    </Grid>
                </Box>

                <SectionTitle>Scheme</SectionTitle>

                <Grid container spacing={1} sx={{ mb: 1.75 }}>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <InfoItem label="Fuel Charge" value={data.fuel_charge} />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <InfoItem label="Pickup Charge" value={data.pickup_charge} />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <InfoItem label="Pickup Scheme" value={data.pickup_scheme} />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <InfoItem label="Quotation" value={data.quotation} />
                    </Grid>
                </Grid>

                <SectionTitle>Compliance</SectionTitle>
                <Grid container spacing={1.25} sx={{ mb: 1.75 }}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <InfoItem label="GST Number" value={data.gst_num} />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <InfoItem label="Status" value={data.status} color={isActive ? green[700] : red[700]} />
                    </Grid>
                </Grid>

                <SectionTitle>Address</SectionTitle>

                <Grid container spacing={1}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <AddressCard title="Billing" address={billingAddress} />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <AddressCard title="Shipping" address={shippingAddress} />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions sx={{ px: 2.25, py: 1.5, borderTop: "1px solid", borderColor: "divider", backgroundColor: grey[50], gap: 0.75 }}>
                <Button variant="outlined" onClick={handleSelfClose} sx={{ textTransform: "none", borderRadius: 1.5 }}>Cancel</Button>
                <Box flex={1} />
                <Button variant="outlined" color="error" sx={{ textTransform: "none", borderRadius: 1.5, }} > Terminate </Button>
                <Button variant="contained" color="primary" onClick={handleEditClose} sx={{ textTransform: "none", borderRadius: 1.5, boxShadow: "none" }} >Edit</Button>
            </DialogActions>
        </Dialog>
    );
}

export default memo(VendorDetails);
